import type {
  Cycle,
  CycleHeaderPhase,
  CycleType,
  PhaseType,
} from "../hooks/useCycles";
import {
  getStepsForCycle,
  type ConditionalCycleOptions,
} from "../../homepage/sections/treatmentFlows";

/**
 * Return the patient-facing step IDs needed by the active-cycle header.
 *
 * Delegates to the treatment flows, which are the one definition of what a
 * cycle's steps are. This used to be a hand-maintained second copy of that
 * graph — a whole switch statement mirroring the hub — because the flows lived
 * inside a React Native component module and a pure presenter could not import
 * them. The copies drifted exactly as you would expect: the header counted
 * seven steps for IVF where the hub showed eight, so the same cycle reported
 * two lengths depending on which surface you looked at.
 *
 * The flows are their own module now, so there is nothing left to mirror. Only
 * the ids are taken; the header has never wanted the copy.
 */
export function getCycleHeaderStepIds(
  cycleType: CycleType,
  protocol: ConditionalCycleOptions["protocol"],
  phases: CycleHeaderPhase[],
  pgtPlan?: ConditionalCycleOptions["pgtPlan"],
  cyclePlanSnapshot?: Cycle["cyclePlanSnapshot"],
): string[] {
  return getStepsForCycle(cycleType, "self", undefined, {
    protocol,
    phases,
    pgtPlan,
    cyclePlanSnapshot,
  }).map((step) => step.id);
}

const STEP_TO_PHASE_TYPE: Record<string, PhaseType> = {
  pre_treatment: "pre_treatment",
  protocol: "protocol",
  stimulation: "protocol",
  trigger_shot: "protocol",
  egg_retrieval: "egg_retrieval",
  freezing: "egg_retrieval",
  fertilization: "fertilization",
  embryo_development: "embryo_development",
  pgt_testing: "embryo_development",
  transfer: "transfer",
  luteal_phase: "luteal_phase",
  sperm_collection: "sperm_collection",
  insemination: "insemination",
  screening: "protocol",
  analysis: "fertilization",
};

export function getCycleHeaderPhaseType(stepId: string): PhaseType | null {
  return STEP_TO_PHASE_TYPE[stepId] ?? null;
}
